import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import type {DraftOrder} from '../types/myaccount';
import api from '../services/api';
import DataTable from './DataTable';
import {accountActionIconGroupStyle, accountActionIconStyle} from './accountActionIconStyles';
import { PageShellLoading, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import CreateDraftOrderModal from './CreateDraftOrderModal';
import MyAccountPageShell from './MyAccountPageShell';

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return dateString;
    }

    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatCurrency = (amount: string): string => {
    const parsedAmount = Number.parseFloat(amount);
    if (Number.isNaN(parsedAmount)) {
        return amount;
    }

    return `$${parsedAmount.toFixed(2)}`;
};

interface MyAccountDraftOrdersProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const MyAccountDraftOrders: React.FC<MyAccountDraftOrdersProps> = ({ wekaPagePermissions = {} }) => {
    const [draftOrders, setDraftOrders] = useState<DraftOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeRequest, setActiveRequest] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const canCreateDraft = wekaPagePermissions.create_draft !== false;
    const canCheckoutDraft = wekaPagePermissions.checkout_draft !== false;
    const canDeleteDraft = wekaPagePermissions.delete_draft !== false;

    const fetchDraftOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getDraftOrders();
            setDraftOrders(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching draft orders:', err);
            setError('Failed to load draft orders. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchDraftOrders();
    }, []);

    const isActionPending = (draftId: number, action: 'delete' | 'convert'): boolean => {
        return activeRequest === `${action}:${draftId}`;
    };

    const handleDeleteDraft = async (draftId: number) => {
        if (!canDeleteDraft) {
            toast.error('Deleting draft orders is disabled for this page.');
            return;
        }

        if (!window.confirm('Delete this draft order?')) {
            return;
        }

        const requestKey = `delete:${draftId}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.deleteDraftOrder(draftId);
            setDraftOrders((currentDrafts) => currentDrafts.filter((draft) => draft.id !== draftId));
            toast.success(response.message);
        } catch (err) {
            console.error('Error deleting draft order:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to delete draft order.');
        } finally {
            setActiveRequest(null);
        }
    };

    const handleCheckoutDraft = async (draftId: number) => {
        if (!canCheckoutDraft) {
            toast.error('Checking out draft orders is disabled for this page.');
            return;
        }

        const requestKey = `convert:${draftId}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.convertDraftOrder(draftId);
            toast.success(response.message);
            window.location.assign(response.checkout_url);
        } catch (err) {
            console.error('Error preparing draft for checkout:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to prepare draft for checkout.');
        } finally {
            setActiveRequest(null);
        }
    };

    if (loading) {
        return (
            <PageShellLoading
                showMeta={true}
                showAction={canCreateDraft}
                titleWidth="24%"
                subtitleWidth="52%"
                metaWidth="120px"
                actionWidth="152px"
            >
                <ToolbarSkeleton />
                <TableSkeleton columns={5} rows={8} />
            </PageShellLoading>
        );
    }

    const filteredDraftOrders = draftOrders.filter((draft) => {
        if (!searchTerm.trim()) {
            return true;
        }

        const normalizedSearchTerm = searchTerm.trim().toLowerCase();
        const formattedDate = formatDate(draft.date_created).toLowerCase();

        return (
            draft.name.toLowerCase().includes(normalizedSearchTerm) ||
            draft.date_created.toLowerCase().includes(normalizedSearchTerm) ||
            formattedDate.includes(normalizedSearchTerm) ||
            draft.total.toLowerCase().includes(normalizedSearchTerm) ||
            draft.items_count.toString().includes(normalizedSearchTerm)
        );
    });

    return (
        <MyAccountPageShell
            title="Draft Orders"
            subtitle="View and manage your saved draft orders."
            meta={filteredDraftOrders.length === draftOrders.length
                ? `${draftOrders.length} ${draftOrders.length === 1 ? 'draft order' : 'draft orders'}`
                : `Showing ${filteredDraftOrders.length} of ${draftOrders.length}`}
            actions={canCreateDraft ? (
                <button
                    type="button"
                    className="button"
                    onClick={() => setIsCreateModalOpen(true)}
                >
                    New Draft Order
                </button>
            ) : undefined}
        >
            {(!canCreateDraft || !canCheckoutDraft || !canDeleteDraft) && (
                <div className="alert alert-info" role="status">
                    {[
                        !canCreateDraft ? 'Creating draft orders is disabled.' : '',
                        !canCheckoutDraft ? 'Draft checkout is disabled.' : '',
                        !canDeleteDraft ? 'Deleting draft orders is disabled.' : '',
                    ].filter(Boolean).join(' ')}
                </div>
            )}

            {error && (
                <div className="woocommerce-error mb-3" role="alert">
                    {error}
                </div>
            )}

            {draftOrders.length === 0 ? (
                <div className="woocommerce-message woocommerce-message--info woocommerce-Message woocommerce-Message--info woocommerce-info mb-4">
                    No draft orders found.
                </div>
            ) : (
                <DataTable
                            rows={filteredDraftOrders}
                            rowKey={(draft) => draft.id}
                            wrapperClassName=""
                            responsiveMode="stacked-cards"
                            tableClassName="table table-hover align-middle mb-0"
                            tableStyle={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                            toolbar={{
                                searchValue: searchTerm,
                                onSearchChange: (value) => {
                                    setSearchTerm(value);
                                    setCurrentPage(1);
                                },
                                entriesPerPage,
                                onEntriesPerPageChange: (value) => {
                                    setEntriesPerPage(value);
                                    setCurrentPage(1);
                                },
                                searchPlaceholder: 'Search draft orders...',
                                filteredCount: filteredDraftOrders.length,
                                totalCount: draftOrders.length,
                                countLabel: 'draft orders',
                                onClearSearch: searchTerm.trim()
                                    ? () => {
                                        setSearchTerm('');
                                        setCurrentPage(1);
                                    }
                                    : undefined,
                            }}
                            pagination={{
                                currentPage,
                                itemsPerPage: entriesPerPage,
                                totalItems: filteredDraftOrders.length,
                                itemsLabel: 'draft orders',
                                onPageChange: setCurrentPage,
                                mode: 'simple',
                            }}
                            rowStyle={{
                                borderBottom: '1px solid var(--weka-subtle-border-color)',
                            }}
                            emptyState={(
                                <div className="text-muted py-3">
                                    {searchTerm.trim() ? 'No draft orders match your search.' : 'No draft orders found.'}
                                </div>
                            )}
                            columns={[
                                {
                                    id: 'draft_order',
                                    header: 'DRAFT ORDER',
                                    mobileLabel: 'Draft order',
                                    mobileDisplay: 'title',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
                                    cellStyle: { padding: '12px' },
                                    render: (draft) => (
                                        <Link
                                            to={`/draft-orders/${draft.id}`}
                                            className="fw-semibold text-decoration-none"
                                        >
                                            {draft.name}
                                        </Link>
                                    ),
                                },
                                {
                                    id: 'date_created',
                                    header: 'DATE CREATED',
                                    mobileLabel: 'Created',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
                                    cellStyle: { padding: '12px' },
                                    render: (draft) => (
                                        <time dateTime={draft.date_created}>{formatDate(draft.date_created)}</time>
                                    ),
                                },
                                {
                                    id: 'items_count',
                                    header: 'ITEMS',
                                    mobileLabel: 'Items',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
                                    cellStyle: { padding: '12px' },
                                    render: (draft) => draft.items_count,
                                },
                                {
                                    id: 'total',
                                    header: 'TOTAL',
                                    mobileLabel: 'Total',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
                                    cellStyle: { padding: '12px' },
                                    render: (draft) => formatCurrency(draft.total),
                                },
                                {
                                    id: 'actions',
                                    header: 'ACTIONS',
                                    mobileDisplay: 'actions',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
                                    cellStyle: { padding: '12px' },
                                    render: (draft) => (
                                        <div style={accountActionIconGroupStyle}>
                                            <Link
                                                to={`/draft-orders/${draft.id}`}
                                                title="Edit draft"
                                                aria-label="Edit draft"
                                                style={accountActionIconStyle}
                                            >
                                                <i className="fas fa-edit" aria-hidden="true"></i>
                                            </Link>
                                            <button
                                                type="button"
                                                className="convert-draft-action"
                                                onClick={() => { void handleCheckoutDraft(draft.id); }}
                                                disabled={activeRequest !== null || !canCheckoutDraft}
                                                title="Checkout draft"
                                                aria-label="Checkout draft"
                                                style={accountActionIconStyle}
                                            >
                                                <i
                                                    className={isActionPending(draft.id, 'convert') ? 'fas fa-spinner fa-spin' : 'fas fa-credit-card'}
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="delete-draft-action"
                                                onClick={() => { void handleDeleteDraft(draft.id); }}
                                                disabled={activeRequest !== null || !canDeleteDraft}
                                                title="Delete draft"
                                                aria-label="Delete draft"
                                                style={accountActionIconStyle}
                                            >
                                                <i
                                                    className={isActionPending(draft.id, 'delete') ? 'fas fa-spinner fa-spin' : 'fas fa-trash'}
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                        </div>
                                    ),
                                },
                            ]}
                        />
            )}
            <CreateDraftOrderModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    setCurrentPage(1);
                    void fetchDraftOrders();
                }}
            />
        </MyAccountPageShell>
    );
};

export default MyAccountDraftOrders;
